import React from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import config from '@core/config';

/**
 * Chat input component with Preline textarea and send button
 * Uses Preline UI auto-height textarea with integrated toolbar
 * Memoized to prevent unnecessary re-renders
 *
 * @param {Object} props
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onKeyDown - Keyboard event handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {boolean} props.disabled - Whether input is disabled
 */

function ChatInputComponent({ value, onChange, onKeyDown, onSubmit, disabled }) {
  const { t } = useTranslation();

  const isDisabled = disabled || !config.apiBaseUrl;

  return (
    <div className="relative">
      <textarea
        id="chat-input"
        name="chat-input"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
        aria-label={t('app.enterQuestion')}
        className="p-3 pb-10 sm:p-4 sm:pb-12 block w-full border border-border-default rounded-xl sm:rounded-2xl text-sm placeholder:text-subtle/80 focus:outline-none focus:border-border-default focus:ring-0 focus:shadow-[0_0_0_3px_rgba(0,0,0,0.05)] disabled:opacity-50 disabled:pointer-events-none resize-none overflow-y-auto max-h-[8rem] sm:max-h-[15rem] md:max-h-[18rem]"
        placeholder={t('app.enterQuestion')}
        rows="2"
        data-hs-textarea-auto-height=""
      />

      {/* Toolbar */}
      <div className="absolute bottom-px inset-x-px p-1.5 sm:p-2 rounded-b-xl sm:rounded-b-2xl bg-surface">
        <div className="flex justify-end items-center gap-x-1">
          {/* Send Button */}
          <button
            type="button"
            onClick={onSubmit}
            disabled={isDisabled}
            aria-label={t('chat.send')}
            aria-disabled={isDisabled}
            className="inline-flex shrink-0 justify-center items-center w-8 h-8 sm:w-9 sm:h-9 rounded-full text-white bg-primary hover:bg-primary-dark focus:outline-none focus:bg-primary-dark disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
          >
            <Icon icon="lucide:arrow-up" className="shrink-0 size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

ChatInputComponent.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

/**
 * Memoized ChatInput to prevent re-renders when parent re-renders
 */
export const ChatInput = React.memo(ChatInputComponent);
